import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { timeline, partyInfo } from '../mockData';
import { History, Target, Users, Award, ArrowRight } from 'lucide-react';
import AppLink from '../components/AppLink';

const About = () => {
  const { language, t } = useLanguage();

  const values = [
    {
      icon: Target,
      title: { en: 'Transparency', bn: 'স্বচ্ছতা' },
      description: {
        en: 'We believe in open, transparent governance where every decision is accountable to the people.',
        bn: 'আমরা উন্মুক্ত, স্বচ্ছ শাসনে বিশ্বাস করি যেখানে প্রতিটি সিদ্ধান্ত জনগণের কাছে জবাবদিহিমূলক।',
      },
    },
    {
      icon: Users,
      title: { en: 'Inclusion', bn: 'অন্তর্ভুক্তি' },
      description: {
        en: 'Every citizen, regardless of background, deserves equal representation and opportunity.',
        bn: 'পটভূমি নির্বিশেষে প্রতিটি নাগরিক সমান প্রতিনিধিত্ব এবং সুযোগের যোগ্য।',
      },
    },
    {
      icon: Award,
      title: { en: 'Excellence', bn: 'উৎকর্ষতা' },
      description: {
        en: 'We strive for excellence in governance, policy-making, and public service.',
        bn: 'আমরা শাসন, নীতি-নির্ধারণ এবং জনসেবায় উৎকর্ষতার জন্য প্রচেষ্টা করি।',
      },
    },
    {
      icon: History,
      title: { en: 'Progress', bn: 'অগ্রগতি' },
      description: {
        en: 'Building a forward-thinking Bangladesh that honors tradition while embracing innovation.',
        bn: 'একটি দূরদর্শী বাংলাদেশ গড়া যা ঐতিহ্যকে সম্মান করে এবং উদ্ভাবনকে গ্রহণ করে।',
      },
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-party-red py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t({ en: 'About Us', bn: 'আমাদের সম্পর্কে' })}
          </h1>
          <p className="text-xl text-red-100 max-w-3xl mx-auto">
            {t(partyInfo.name)}
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {t({ en: 'Our Mission', bn: 'আমাদের লক্ষ্য' })}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                {t({
                  en: 'Founded on the principles of integrity, inclusion, and development, the National Democratic Party (NDP) is a progressive political movement committed to transforming governance from the grassroots level.',
                  bn: 'সততা, অন্তর্ভুক্তি ও উন্নয়নের নীতিতে প্রতিষ্ঠিত, ন্যাশনাল ডেমোক্রেটিক পার্টি (এনডিপি) একটি অগ্রগতিমুখী রাজনৈতিক আন্দোলন যা তৃণমূল স্তর থেকে শাসন ব্যবস্থার রূপান্তরের জন্য প্রতিশ্রুতিবদ্ধ।',
                })}
              </p>
              <p className="text-gray-600 leading-relaxed">
                {t({
                  en: 'We aim to establish a new political culture—where transparency, young leadership, gender equality, sustainable development, and innovation in governance are prioritized.',
                  bn: 'আমাদের লক্ষ্য একটি নতুন রাজনৈতিক সংস্কৃতি গড়ে তোলা—যেখানে স্বচ্ছতা, তরুণ নেতৃত্ব, নারী-পুরুষ সমতা, টেকসই উন্নয়ন এবং শাসনে উদ্ভাবনকে অগ্রাধিকার দেওয়া হয়।',
                })}
              </p>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {t({ en: 'Our Vision', bn: 'আমাদের দৃষ্টিভঙ্গি' })}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                {t({
                  en: 'Building a democratic, inclusive, and prosperous Bangladesh where every citizen has equal opportunity, justice, and the chance to live with dignity.',
                  bn: 'একটি গণতান্ত্রিক, অন্তর্ভুক্তিমূলক এবং সমৃদ্ধ বাংলাদেশ গড়া যেখানে প্রতিটি নাগরিকের সমান সুযোগ, ন্যায়বিচার এবং সম্মানের সাথে বেঁচে থাকার সুযোগ রয়েছে।',
                })}
              </p>
              <p className="text-gray-600 leading-relaxed">
                {t({
                  en: 'We are a diverse coalition of educators, entrepreneurs, social workers, farmers, professionals, and young leaders who believe politics is not about power, but about service.',
                  bn: 'আমরা শিক্ষক, উদ্যোক্তা, সমাজকর্মী, কৃষক, পেশাজীবী এবং তরুণ নেতাদের একটি বৈচিত্র্যপূর্ণ জোট যারা বিশ্বাস করি রাজনীতি ক্ষমতার নয়, সেবার মাধ্যম।',
                })}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            {t({ en: 'Our Core Values', bn: 'আমাদের মূল মূল্যবোধ' })}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => (
              <Card key={idx} className="text-center hover:shadow-xl transition-shadow duration-300">
                <CardHeader>
                  <div className="mx-auto w-16 h-16 bg-party-red-100 rounded-full flex items-center justify-center mb-4">
                    <value.icon className="h-8 w-8 text-party-red" />
                  </div>
                  <CardTitle>{t(value.title)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{t(value.description)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
            {t({ en: 'Our Journey', bn: 'আমাদের যাত্রা' })}
          </h2>
          <p className="text-center text-gray-500 mb-12">
            {t({ en: '37 Years of Nationalist Politics (1989–2026)', bn: '৩৭ বছরের জাতীয়তাবাদী রাজনীতি (১৯৮৯–২০২৬)' })}
          </p>
          <div className="relative">
            {/* Center line — hidden on mobile */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-party-red via-party-red/60 to-party-green rounded-full"></div>
            {/* Left line — mobile only */}
            <div className="md:hidden absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-party-red via-party-red/60 to-party-green rounded-full"></div>
            <div className="space-y-8 md:space-y-12">
              {timeline.map((item, idx) => (
                <div key={idx} className={`flex items-start md:items-center ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-testid={`timeline-${item.year}`}>
                  {/* Mobile layout: always left-aligned */}
                  <div className="md:hidden flex items-start">
                    <div className="relative w-8 h-8 bg-party-red rounded-full border-4 border-white shadow-lg z-10 shrink-0 mr-4 mt-1">
                      <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] font-bold text-party-red whitespace-nowrap">{item.year}</span>
                    </div>
                    <Card className="flex-1 hover:shadow-xl transition-shadow duration-300">
                      <CardContent className="p-4">
                        <div className="text-lg font-bold text-party-red mb-1">{item.year}</div>
                        <h3 className="text-base font-bold text-gray-900 mb-1">{t(item.title)}</h3>
                        <p className="text-sm text-gray-600">{t(item.description)}</p>
                      </CardContent>
                    </Card>
                  </div>
                  {/* Desktop layout: alternating sides */}
                  <div className={`hidden md:block w-1/2 ${idx % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <Card className="hover:shadow-xl transition-shadow duration-300 hover:-translate-y-1 transition-transform">
                      <CardContent className="p-6">
                        <div className="text-2xl font-bold text-party-red mb-2">{item.year}</div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{t(item.title)}</h3>
                        <p className="text-gray-600">{t(item.description)}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="hidden md:block relative w-8 h-8 bg-party-red rounded-full border-4 border-white shadow-lg z-10 shrink-0"></div>
                  <div className="hidden md:block w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white" data-testid="about-flag-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-8 md:grid-cols-[280px_1fr] items-center">
          <svg viewBox="0 0 800 500" className="w-full rounded-md shadow-lg ring-1 ring-gray-200" aria-hidden="true">
            <rect width="800" height="150" fill="#E31E24" /><rect y="150" width="800" height="200" fill="#fff" /><rect y="350" width="800" height="150" fill="#0E9A49" />
            <mask id="about-crescent"><rect width="800" height="500" fill="white" /><circle cx="178" cy="250" r="66" fill="black" /></mask>
            <circle cx="150" cy="250" r="78" fill="#0E9A49" mask="url(#about-crescent)" />
          </svg>
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">{t({ en: 'Our Flag', bn: 'আমাদের পতাকা' })}</h2>
            <p className="text-gray-600 mb-5">{t({ en: 'Red for sacrifice and courage, white for peace and unity, green for the land and its people, and a rising crescent for faith and a new beginning.', bn: 'লাল ত্যাগ ও সাহসের, সাদা শান্তি ও ঐক্যের, সবুজ মাটি ও মানুষের, আর উদীয়মান নবচন্দ্র বিশ্বাস ও নতুন সূচনার প্রতীক।' })}</p>
            <AppLink to="/our-flag" className="inline-flex items-center gap-2 font-semibold text-party-red hover:underline" data-testid="about-flag-link">{t({ en: 'Read the significance of the flag', bn: 'পতাকার তাৎপর্য পড়ুন' })}<ArrowRight className="h-4 w-4" /></AppLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;